import { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";
import { GenerateQuoteCategory } from "../enums/GenerateQuoteCategory";
import {
    IDigitalQuestion,
    IQuestionOptionImage,
    IRecommendedCoverResults
} from "../interfaces/IDigitalAdviceData";

const incomeTaxQuestionId = 13;

interface IQuestionImages {
    questionId: number;
    frameImages: Array<IQuestionOptionImage>;
}

interface IChangeFrequency {
    recommendedCoverType: string;
    frequency: string;
    isPrimaryAdult: boolean;
    adviceAmount?: number;
}

interface IDigitalGuoteGroupIDs {
    Interim?: string;
    Budget?: string;
    Goals?: string;
    Ultimate?: string;
}

export interface IAdviceQuestionStateProps {
    sessionUserId: string;
    visitorId: string;
    questionList: Array<IDigitalQuestion>;
    baseImages: Array<IQuestionOptionImage>;
    frameImages: Array<IQuestionImages>;
    isFinalRecommendedSet?: boolean;
    finalRecommendedCover: IRecommendedCoverResults;
    frequencyAdviceAmount: Array<IChangeFrequency>;
    nextQuestionOrder?: number;
    previousQuestionId?: number;
    quoteGroupIDs?: IDigitalGuoteGroupIDs;
}

interface IAdviceQuestionSelectors {
    getSessionUserId?: () => string;
    getVisitorId?: () => string;
    checkQuestion?: (questionId: number) => boolean;
    getQuestion?: (questionId: number) => IDigitalQuestion;
    getFrameBaseImages?: () => IQuestionOptionImage[];
    getFrameImages?: (questionId: number) => IQuestionOptionImage[];
    checkFinalRecommendedCover?: () => boolean;
    getFinalRecommendedCover?: () => IRecommendedCoverResults;
    getFrequencyAdviceAmount?: (data: IChangeFrequency) => IChangeFrequency;
    getNextQuestionOrder?: () => number;
    getPreviousQuestionId?: () => number;
    getDigitalQuoteGroupIDs: (category: GenerateQuoteCategory) => string;
}

interface IAdviceQuestionActions {
    reset: () => Promise<void>;
    resetFrameImages: () => void;
    setSessionUserId: (sessionUserId: string) => void;
    setVisitorId: (visitorId: string) => void;
    setQuestion: (question: IDigitalQuestion) => void;
    setFrameBaseImages: (baseImages) => void;
    setFrameImages: (data: IQuestionImages) => void;
    setFinalRecommendedCover: (results: IRecommendedCoverResults) => void;
    setFrequencyAdviceAmount: (data: IChangeFrequency) => void;
    setNextQuestionOrder: (nextQuestionOrder: number) => void;
    setPreviousQuestionId: (previousQuestionId: number) => void;
    setDigitalQuoteGroupIDs: (
        category: GenerateQuoteCategory,
        quoteGroupID: string
    ) => void;
}

interface IAdviceQuestionState {
    selectors: IAdviceQuestionSelectors;
    actions: IAdviceQuestionActions;
}

const useAdviceQuestionState = (
    defaultState: IAdviceQuestionStateProps
): IAdviceQuestionState => {
    const [state, setState] = useImmer<IAdviceQuestionStateProps>(defaultState);

    useEffect(() => {
        const stringState = JSON.stringify(state);
        window.localStorage.setItem("dap_states", stringState);
    }, [state]);

    return {
        selectors: {
            getSessionUserId: (): string => {
                return state.sessionUserId;
            },
            getVisitorId: (): string => {
                return state.visitorId;
            },
            checkQuestion: (questionId: number): boolean => {
                const questionList =
                    JSON.parse(window.localStorage.getItem("dap_states"))?.questionList ||
                    [];
                const question = questionList.find((i) => i.id === questionId);
                return !!(question && question.id) || false;
            },
            getQuestion: (questionId: number): IDigitalQuestion => {
                const questionList =
                    JSON.parse(window.localStorage.getItem("dap_states"))?.questionList ||
                    [];
                return questionList.find((i) => i.id === questionId);
            },
            getFrameBaseImages: (): IQuestionOptionImage[] => {
                return state.baseImages || [];
            },
            getFrameImages: (questionId: number): IQuestionOptionImage[] => {
                const frameImages =
                    JSON.parse(window.localStorage.getItem("dap_states"))?.frameImages ||
                    [];
                const find = frameImages.find((i) => i.questionId === questionId);
                return (find && find.frameImages) || [];
            },
            checkFinalRecommendedCover: (): boolean => {
                return state.isFinalRecommendedSet;
            },
            getFinalRecommendedCover: (): IRecommendedCoverResults => {
                return state.finalRecommendedCover;
            },
            getFrequencyAdviceAmount: (data: IChangeFrequency): IChangeFrequency => {
                const { recommendedCoverType, frequency, isPrimaryAdult } = data;
                return (
                    state.frequencyAdviceAmount &&
                    state.frequencyAdviceAmount.length &&
                    state.frequencyAdviceAmount.find(
                        (i) =>
                            i.recommendedCoverType === recommendedCoverType &&
                            i.frequency === frequency &&
                            i.isPrimaryAdult === isPrimaryAdult
                    )
                );
            },
            getNextQuestionOrder: (): number => {
                const nextQuestionOrder =
                    JSON.parse(window.localStorage.getItem("dap_states"))
                        ?.nextQuestionOrder || 0;
                return state.nextQuestionOrder || nextQuestionOrder;
            },
            getPreviousQuestionId: (): number => {
                const previousQuestionId =
                    JSON.parse(window.localStorage.getItem("dap_states"))
                        ?.previousQuestionId || 0;
                return state.previousQuestionId || previousQuestionId;
            },
            getDigitalQuoteGroupIDs: (category: GenerateQuoteCategory): string => {
                const quoteGroupIDs = JSON.parse(
                    window.localStorage.getItem("dap_states")
                )?.quoteGroupIDs;
                switch (category) {
                    case GenerateQuoteCategory.INTERIM:
                        return (
                            quoteGroupIDs?.Interim || state.quoteGroupIDs?.Interim || ""
                        );
                    case GenerateQuoteCategory.BUDGET:
                        return quoteGroupIDs?.Budget || state.quoteGroupIDs?.Budget || "";
                    case GenerateQuoteCategory.GOALS:
                        return quoteGroupIDs?.Goals || state.quoteGroupIDs?.Goals || "";
                    case GenerateQuoteCategory.ULTIMATE:
                        return (
                            quoteGroupIDs?.Ultimate || state.quoteGroupIDs?.Ultimate || ""
                        );
                    default:
                        return "";
                }
            }
        },
        actions: useMemo(
            () => ({
                reset: async (): Promise<void> => {
                    window.localStorage.removeItem("dap_states");
                    window.localStorage.removeItem("dap_process");
                    return setState((advice) => {
                        advice.sessionUserId = "";
                        advice.visitorId = "";
                        advice.questionList = [];
                        advice.baseImages = [];
                        advice.frameImages = [];
                        advice.finalRecommendedCover = {
                            budget: {
                                client: {},
                                partner: {}
                            },
                            goals: {
                                client: {},
                                partner: {}
                            },
                            ultimate: {
                                client: {},
                                partner: {}
                            }
                        };
                        advice.frequencyAdviceAmount = [];
                        advice.isFinalRecommendedSet = false;
                        advice.nextQuestionOrder = null;
                        advice.previousQuestionId = null;
                        advice.quoteGroupIDs = {
                            Interim: null,
                            Budget: null,
                            Goals: null,
                            Ultimate: null
                        };
                    });
                },
                resetFrameImages: (): void => {
                    setState((advice) => {
                        advice.frameImages = [];
                    });
                },
                setSessionUserId: (sessionUserId: string): void => {
                    if (!sessionUserId) return;
                    setState((advice) => {
                        advice.sessionUserId = sessionUserId;
                    });
                },
                setVisitorId: (visitorId: string): void => {
                    if (!visitorId) return;
                    setState((advice) => {
                        advice.visitorId = visitorId;
                    });
                },
                setQuestion: (question: IDigitalQuestion): void => {
                    const questionList =
                        JSON.parse(window.localStorage.getItem("dap_states"))
                            ?.questionList || state.questionList;
                    const findQuestion = questionList.find((i) => i.id === question.id);
                    if (!findQuestion) {
                        setState((advice) => {
                            advice.questionList.push(question);
                        });
                    }
                },
                setFrameBaseImages: (baseImages): void => {
                    setState((advice) => {
                        advice.baseImages = baseImages;
                    });
                },
                setFrameImages: (data: IQuestionImages): void => {
                    const frameImages =
                        JSON.parse(window.localStorage.getItem("dap_states"))
                            ?.frameImages || [];
                    const find = frameImages.find(
                        (i) => i.questionId === data.questionId
                    );
                    if (!find) {
                        setState((advice) => {
                            advice.frameImages.push(data);
                        });
                        return;
                    }
                    if (find && data.questionId === incomeTaxQuestionId) {
                        const filter = state.frameImages.filter(
                            (i) => i.questionId <= data.questionId
                        );
                        setState((advice) => {
                            advice.frameImages = [...filter, data];
                        });
                    }
                },
                setFinalRecommendedCover: (results: IRecommendedCoverResults): void => {
                    if (results && results.budget) {
                        setState((advice) => {
                            advice.isFinalRecommendedSet = true;
                            advice.finalRecommendedCover = results;
                        });
                    }
                },
                setFrequencyAdviceAmount: (data: IChangeFrequency): void => {
                    const { recommendedCoverType, frequency, isPrimaryAdult } = data;
                    const find =
                        state.frequencyAdviceAmount &&
                        state.frequencyAdviceAmount.length &&
                        state.frequencyAdviceAmount.find(
                            (i) =>
                                i.recommendedCoverType === recommendedCoverType &&
                                i.frequency === frequency &&
                                i.isPrimaryAdult === isPrimaryAdult
                        );

                    if (!find || !find.adviceAmount) {
                        if (
                            !state.frequencyAdviceAmount ||
                            !state.frequencyAdviceAmount.length
                        ) {
                            setState((advice) => {
                                advice.frequencyAdviceAmount = [data];
                            });
                        } else {
                            setState((advice) => {
                                advice.frequencyAdviceAmount.push(data);
                            });
                        }
                    }
                },
                setNextQuestionOrder: (nextQuestionOrder: number): void => {
                    setState((advice) => {
                        advice.nextQuestionOrder = nextQuestionOrder;
                    });
                },
                setPreviousQuestionId: (previousQuestionId: number): void => {
                    setState((advice) => {
                        advice.previousQuestionId = previousQuestionId;
                    });
                },
                setDigitalQuoteGroupIDs: (
                    category: GenerateQuoteCategory,
                    quoteGroupID: string
                ): void => {
                    setState((advice) => {
                        switch (category) {
                            case GenerateQuoteCategory.INTERIM:
                                advice.quoteGroupIDs.Interim = quoteGroupID;
                                break;
                            case GenerateQuoteCategory.BUDGET:
                                advice.quoteGroupIDs.Budget = quoteGroupID;
                                break;
                            case GenerateQuoteCategory.GOALS:
                                advice.quoteGroupIDs.Goals = quoteGroupID;
                                break;
                            case GenerateQuoteCategory.ULTIMATE:
                                advice.quoteGroupIDs.Ultimate = quoteGroupID;
                                break;
                            default:
                        }
                    });
                }
            }),
            [setState]
        )
    };
};

export default useAdviceQuestionState;
